import { useState } from "react";
import { toast } from "@/components/ui/sonner";
import { useNavigate } from "react-router-dom";
import { Share2, Mail, Link as LinkIcon, MessageCircle, MessageSquare, Facebook, Linkedin } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/appresse";
import { useAuth } from "@/lib/auth";
import { useReader } from "@/components/Layout";

// Crawler-friendly share URL: the backend serves per-edition Open Graph tags
// (so WhatsApp/SMS previews show THIS magazine's cover) and then redirects real
// visitors into the in-app reader. The partner ref is carried through so the
// whole re-share chain keeps tracing back to the original partner.
const shareLink = (edition, ref) =>
  `${window.location.origin}/api/share/${edition.id}${ref ? `?ref=${encodeURIComponent(ref)}` : ""}`;

const dateFr = (d) => { try { return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long" }); } catch { return ""; } };
const brandOf = (e) => (e.title || "").split(" — ")[0];
// Un seul message, cohérent sur tous les canaux.
export const shareText = (e, user) => {
  const who = user?.first_name ? `${user.first_name} vous partage` : "On vous partage";
  const when = e.publication_date ? ` du ${dateFr(e.publication_date)}` : "";
  return `📰 ${who} « ${brandOf(e)} »${when} sur APP PRESSE.\n👉 Touchez le lien pour l'ouvrir et le lire tout de suite :`;
};

const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231L18.244 2.25Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);

const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle,
    url: (e, ref, u) => `https://wa.me/?text=${encodeURIComponent(`${shareText(e, u)}\n${shareLink(e, ref)}`)}` },
  { key: "sms", label: "SMS", icon: MessageSquare,
    url: (e, ref, u) => `sms:?&body=${encodeURIComponent(`${shareText(e, u)} ${shareLink(e, ref)}`)}` },
  { key: "email", label: "E-mail", icon: Mail,
    url: (e, ref, u) => `mailto:?subject=${encodeURIComponent(`📰 ${brandOf(e)} vous a été partagé sur APP PRESSE`)}&body=${encodeURIComponent(`${shareText(e, u)}\n${shareLink(e, ref)}`)}` },
  { key: "facebook", label: "Facebook", icon: Facebook,
    url: (e, ref) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink(e, ref))}` },
  { key: "x", label: "X", icon: XIcon,
    url: (e, ref, u) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText(e, u))}&url=${encodeURIComponent(shareLink(e, ref))}` },
  { key: "linkedin", label: "LinkedIn", icon: Linkedin,
    url: (e, ref) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareLink(e, ref))}` },
];

export const ShareMenu = ({ edition, variant = "outline", size = "sm", dark = false, label = false }) => {
  const { user } = useAuth();
  const { requireAccount } = useReader();
  const [open, setOpen] = useState(false);

  const track = (channel) => api.track({ type: "share", edition_id: edition.id, channel })
    .then(() => window.dispatchEvent(new Event("appresse:ambassador-refresh")))
    .catch(() => {});

  const onChannel = (ch) => {
    if (!user) {
      requireAccount("Créez un compte gratuit pour partager");
      return;
    }
    track(ch.key);
    const href = ch.url(edition, user?.ref_code, user);
    if (href.startsWith("http")) {
      window.open(href, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = href;
    }
    setOpen(false);
    toast.success(`Partagé via ${ch.label}`);
  };

  const onCopy = async () => {
    if (!user) {
      requireAccount("Créez un compte gratuit pour partager");
      return;
    }
    try {
      await navigator.clipboard.writeText(`${shareText(edition, user)}\n${shareLink(edition, user?.ref_code)}`);
      track("copy");
      toast.success("Lien copié");
    } catch {
      toast.error("Impossible de copier le lien");
    }
    setOpen(false);
  };

  // Native OS share sheet (mobile): opens WhatsApp/SMS/Messages… directly with
  // the ref-tagged link (carries the Open Graph preview image) + a short text.
  const canNative = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const nativeShare = async (e) => {
    e?.stopPropagation?.();
    if (!user) {
      requireAccount("Créez un compte gratuit pour partager");
      return;
    }
    const url = shareLink(edition, user?.ref_code);
    try {
      await navigator.share({
        title: `📰 ${brandOf(edition)} — APP PRESSE`,
        text: shareText(edition, user),
        url,
      });
      track("native");
    } catch {
      /* user cancelled the share sheet — no error */
    }
  };

  if (canNative) {
    return (
      <Button
        type="button"
        size={size}
        variant={variant}
        className={`gap-1.5 ${size === "icon" ? "h-8 w-8" : ""} ${dark ? "text-slate-200 hover:text-white hover:bg-slate-800" : ""}`}
        data-testid={`share-button-${edition.id}`}
        aria-label="Partager"
        onClick={nativeShare}
      >
        <Share2 className="w-4 h-4" />
        {label && <span className="hidden sm:inline">Partager</span>}
      </Button>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size={size}
          variant={variant}
          className={`gap-1.5 ${size === "icon" ? "h-8 w-8" : ""} ${dark ? "text-slate-200 hover:text-white hover:bg-slate-800" : ""}`}
          data-testid={`share-button-${edition.id}`}
          aria-label="Partager"
          onClick={(e) => e.stopPropagation()}
        >
          <Share2 className="w-4 h-4" />
          {label && <span className="hidden sm:inline">Partager</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48" onClick={(e) => e.stopPropagation()}>
        {CHANNELS.map((ch) => (
          <DropdownMenuItem key={ch.key} onClick={() => onChannel(ch)} data-testid={`share-${ch.key}-${edition.id}`}>
            <ch.icon className="w-4 h-4 mr-2" /> {ch.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem onClick={onCopy} data-testid={`share-copy-${edition.id}`}>
          <LinkIcon className="w-4 h-4 mr-2" /> Copier le lien
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
